import { atom } from 'jotai'
import { atomFamily, atomWithStorage } from 'jotai/utils'

/**
 * 配送依頼ドロワーの並び替えキー（#4609 VOC_PJ-970-1）
 * BE の許可キー（SHIPMENT_ORDER_SORT_CONFIG）に合わせる
 */
export const DRAWER_SORT_KEYS = ['orderNo', 'createdAt', 'updatedAt'] as const
export type DrawerSortKey = (typeof DRAWER_SORT_KEYS)[number]

export const DRAWER_SORT_ORDERS = ['ASC', 'DESC'] as const
export type DrawerSortOrder = (typeof DRAWER_SORT_ORDERS)[number]

export type DrawerSort = { key: DrawerSortKey; order: DrawerSortOrder }

/** 既定は配送依頼No 昇順（現行の実効の並びと一致する） */
export const DEFAULT_DRAWER_SORT: DrawerSort = { key: 'orderNo', order: 'ASC' }

const getDrawerSortStorageKey = (userId: number) =>
  `dispatch-board-drawer-sort:${String(userId)}`

/** 保存値が壊れている場合（手で書き換えた、旧版の値が残っている）は既定へ落とす */
const normalize = (value: unknown): DrawerSort => {
  if (typeof value !== 'object' || value === null) return DEFAULT_DRAWER_SORT

  const { key, order } = value as Partial<DrawerSort>
  if (!DRAWER_SORT_KEYS.includes(key as DrawerSortKey)) {
    return DEFAULT_DRAWER_SORT
  }
  if (!DRAWER_SORT_ORDERS.includes(order as DrawerSortOrder)) {
    return DEFAULT_DRAWER_SORT
  }

  return { key: key as DrawerSortKey, order: order as DrawerSortOrder }
}

/** 既定と同じ並びかどうか（既定時はリクエストに sort / order を積まない） */
export const isDefaultDrawerSort = (sort: DrawerSort): boolean =>
  sort.key === DEFAULT_DRAWER_SORT.key &&
  sort.order === DEFAULT_DRAWER_SORT.order

type Params = { userId: number }

/**
 * 配送依頼ドロワーの並び順設定Atom（ユーザー単位）
 * - localStorage で永続化
 * - DC・発送日を切り替えても引き継ぐ（atomFamily の比較は userId のみ）
 * - 別 PC・別ブラウザでは既定（配送依頼No 昇順）から始まる
 */
export const drawerShipmentOrderSortAtomFamily = atomFamily(
  (params: Params) => {
    const storageKey = getDrawerSortStorageKey(params.userId)

    // getOnInit: true — 初回描画から保存値を使う。
    // false（既定）だとマウントまで既定値のままとなり、保存済みの並びが効く前に
    // 既定の並びで 1 回問い合わせが走ってしまう
    const base = atomWithStorage<DrawerSort>(
      storageKey,
      DEFAULT_DRAWER_SORT,
      undefined,
      {
        getOnInit: true
      }
    )

    return atom(
      (get) => normalize(get(base)),
      (_get, set, next: DrawerSort) => set(base, next)
    )
  },
  (a, b) => a.userId === b.userId
)
